import { useCallback, useEffect, useRef, useState } from 'react';
import type { Engine } from '@core/engine';
import { stepWorld } from '@core/engine';
import type { SaveStorage } from '@platform/SaveStorage';
import { makeSaveBlob } from '@core/save/serialize';
import { exportCode, importCode, ImportError } from '@core/save/codec';
import { applyOffline, type OfflineReport } from '@core/sim/offline';

const AUTOSAVE_MS = 15_000;

const BACKUP_KEY = 'bme.lastBackupAt';
/** Nudge the player to export a backup after this long without one. */
export const BACKUP_NUDGE_MS = 24 * 60 * 60 * 1000;

export interface SaveManager {
  lastSavedAt: number | null;
  lastBackupAt: number | null;
  needsBackup: boolean;
  offlineReport: OfflineReport | null;
  dismissOffline: () => void;
  saveNow: () => Promise<void>;
  doExport: () => string;
  doImport: (code: string) => { ok: boolean; message: string };
  hardReset: () => Promise<void>;
}

function readBackupAt(): number | null {
  try {
    const v = localStorage.getItem(BACKUP_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

/**
 * Owns the engine<->storage lifecycle on the UI side: applies offline progress
 * on boot, autosaves on an interval and on tab-hide/unload, and exposes
 * export/import. The core stays pure; all wall-clock reads (Date.now) live here.
 */
export function useSaveManager(engine: Engine, storage: SaveStorage, booted: boolean): SaveManager {
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [lastBackupAt, setLastBackupAt] = useState<number | null>(() => readBackupAt());
  const [offlineReport, setOfflineReport] = useState<OfflineReport | null>(null);
  const offlineHandled = useRef(false);

  const saveNow = useCallback(async () => {
    const now = Date.now();
    engine.getState().lastTickAt = now;
    await storage.save(makeSaveBlob(engine.getState(), now));
    setLastSavedAt(now);
  }, [engine, storage]);

  // Apply offline progress once, after the engine has loaded a save.
  useEffect(() => {
    if (!booted || offlineHandled.current) return;
    offlineHandled.current = true;
    const report = applyOffline(engine.getState(), Date.now(), stepWorld);
    engine.setState(engine.getState()); // notify subscribers of the credited gains
    // Only celebrate a meaningful absence (avoid a modal for a 1-2s reload gap).
    if (report.creditedMs > 60_000) setOfflineReport(report);
  }, [booted, engine]);

  // Autosave on an interval.
  useEffect(() => {
    if (!booted) return;
    const id = setInterval(() => void saveNow(), AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [booted, saveNow]);

  // Save on tab-hide and unload (durability, TECHNICAL_APPROACH §10).
  useEffect(() => {
    if (!booted) return;
    const onHide = () => void saveNow();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, [booted, saveNow]);

  const doExport = useCallback(() => {
    const now = Date.now();
    try {
      localStorage.setItem(BACKUP_KEY, String(now));
    } catch {
      /* storage may be unavailable */
    }
    setLastBackupAt(now);
    return exportCode(engine.getState(), now);
  }, [engine]);

  const doImport = useCallback(
    (code: string): { ok: boolean; message: string } => {
      try {
        const next = importCode(code);
        next.lastTickAt = Date.now();
        engine.setState(next);
        void saveNow();
        return { ok: true, message: 'Save imported. Welcome back, operator.' };
      } catch (e) {
        const msg = e instanceof ImportError ? e.message : 'Import failed.';
        return { ok: false, message: msg };
      }
    },
    [engine, saveNow],
  );

  const hardReset = useCallback(async () => {
    await storage.clear();
    window.location.reload();
  }, [storage]);

  const dismissOffline = useCallback(() => setOfflineReport(null), []);

  const needsBackup = booted && (lastBackupAt === null || Date.now() - lastBackupAt > BACKUP_NUDGE_MS);

  return { lastSavedAt, lastBackupAt, needsBackup, offlineReport, dismissOffline, saveNow, doExport, doImport, hardReset };
}
